'use client'

import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import { isEqual } from 'lodash'
import { v4 as uuid } from 'uuid'
import { Cell, Divider, EmptyState, InputText, Text } from '@vinted/web-ui'

import { useTrackAbTestCallback } from '@marketplace-web/feature-flags/ab-tests-data'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'
import {
  flattenDynamicFilterOptions,
  transformDynamicFilterDefaultOptions,
  DynamicFilterOptionModel,
  ExperimentModel,
  LinksModel,
  SearchTranslationsModel,
  DynamicExposeType,
  OptionType,
  SelectionType,
  Filter,
  trackExperimentsByType,
  SELECT_LARGER_DROPDOWN_MIN_WIDTH,
  SELECT_LARGER_DROPDOWN_MAX_HEIGHT,
} from '@marketplace-web/catalog/catalog-data'
import {
  useCatalogContext,
  DynamicFilterReducerAction,
} from '@marketplace-web/catalog/catalog-page-data'

import { clickFilterEvent } from '_libs/common/event-tracker/events'
import {
  userClickDynamicFilterOptionEvent,
  userViewDynamicFilterOptionEvent,
} from '@marketplace-web/catalog/catalog-filters-data'
import { Screen } from 'constants/tracking/screens'

import useFetchDynamicFilterSearch from '../../../../../../hooks/useFetchDynamicFilterSearch'
import SelectableItemList, {
  RenderItemProps,
  SelectableListItem,
} from '../../../SelectableItemList'
import FilterDropdown from '../../../FilterDropdown'
import DynamicFilterCell from '../../common/DynamicFilterCell'
import FilterSelectionButton from '../../../FilterSelectionButton'

type ListOptionItem = SelectableListItem<DynamicFilterOptionModel>

type Props = {
  type: string
  title: string
  selectionType: SelectionType
  isSelectionHighlighted?: boolean
  isHidden?: boolean
  experiments?: Array<ExperimentModel>
  rootOptionType: OptionType
  links?: Array<LinksModel>
  options: Array<DynamicFilterOptionModel>
  selectedIds: Array<number>
  fromScreen: Screen
  translations?: SearchTranslationsModel
  totalItemsCount?: number | null
  onFilterOpen: (type: string) => void
  onListOptionSelect: (id: number) => void
  onSubmit: () => void
}

const SearchFilterDropdown = ({
  type,
  title,
  selectionType,
  isSelectionHighlighted,
  isHidden,
  experiments,
  rootOptionType,
  links,
  options,
  selectedIds,
  fromScreen,
  translations,
  totalItemsCount,
  onFilterOpen,
  onListOptionSelect,
  onSubmit,
}: Props) => {
  const { track } = useTracking()
  const trackAbTest = useTrackAbTestCallback()
  const fetchFilterSearch = useFetchDynamicFilterSearch()
  const {
    dispatch,
    state: {
      searchSession,
      dynamicFilters: { filterSearchQuery },
    },
  } = useCatalogContext()
  const { globalSearchSessionId, globalCatalogBrowseSessionId } = searchSession
  const searchSessionId = searchSession.sessionId
  const seenItems = useRef<Array<number>>([])
  const [query, setQuery] = useState<string | null>(null)
  const [highlightedSelectedIds, setHighlightedSelectedIds] = useState<Array<number>>(selectedIds)
  const [forceClose, setForceClose] = useState(false)
  const [initialSelectedIds, setInitialSelectedIds] = useState<Array<number>>(selectedIds)
  const filteringSessionId = useMemo(() => uuid(), [])

  useEffect(() => {
    if (experiments) trackExperimentsByType(experiments, DynamicExposeType.View, trackAbTest)
  }, [trackAbTest, experiments])

  const flattedOptions = useMemo(() => {
    return flattenDynamicFilterOptions(options)
  }, [options])

  const listItems = useMemo(() => {
    return transformDynamicFilterDefaultOptions(
      options,
      rootOptionType,
      isSelectionHighlighted,
      highlightedSelectedIds,
    )
  }, [options, rootOptionType, isSelectionHighlighted, highlightedSelectedIds])

  const getCurrentPosition = (id: number) =>
    flattedOptions.findIndex(option => option.id === id) + 1

  const handleOnFilterClick = () => {
    track(
      clickFilterEvent({
        filter: Filter.Dynamic,
        screen: Screen.CatalogFilters,
        searchSessionId,
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
        type,
        code: type,
      }),
    )

    if (experiments) trackExperimentsByType(experiments, DynamicExposeType.Click, trackAbTest)
  }

  const handleItemClick = (item: ListOptionItem) => {
    const id = Number(item.id)
    const position = getCurrentPosition(id)
    const itemsCount = item.data?.itemsCount

    onListOptionSelect(id)

    track(
      userClickDynamicFilterOptionEvent({
        id,
        type,
        code: type,
        position,
        isSelected: !selectedIds.includes(id),
        itemsCount,
        searchText: query || '',
        filteringSessionId,
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
        searchSessionId,
        fromScreen,
      }),
    )

    if (experiments) trackExperimentsByType(experiments, DynamicExposeType.Apply, trackAbTest)
  }

  const getHandleItemView = (item: ListOptionItem) => (inView: boolean) => {
    if (!inView) return

    const id = Number(item.id)
    if (seenItems.current.includes(id)) return

    const position = getCurrentPosition(id)

    track(
      userViewDynamicFilterOptionEvent({
        id,
        type,
        code: type,
        position,
        itemsCount: item.data?.itemsCount,
        searchText: query || '',
        filteringSessionId,
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
        searchSessionId,
        fromScreen,
      }),
    )

    seenItems.current = [...seenItems.current, id]
  }

  const handleOpenDropdown = () => {
    seenItems.current = []
    setForceClose(false)
    setInitialSelectedIds(selectedIds)

    onFilterOpen(type)
  }

  const handleCloseDropdown = () => {
    setHighlightedSelectedIds(selectedIds)
    setForceClose(true)
    onSubmit()

    if (filterSearchQuery.code) {
      dispatch({
        type: DynamicFilterReducerAction.SET_FILTER_SEARCH_QUERY,
        payload: { text: null, code: null },
      })
    }

    if (query) setQuery('')
  }

  const handleAfterClose = () => {
    const wasForceClosed = forceClose
    setForceClose(false)
    setHighlightedSelectedIds(selectedIds)
    if (!isEqual(selectedIds, initialSelectedIds) && !wasForceClosed) {
      onSubmit()
    }
  }

  const handleSearchInput = (event: ChangeEvent<HTMLInputElement>) => {
    const searchQuery = event.currentTarget.value
    setQuery(searchQuery)

    if (searchQuery) {
      fetchFilterSearch({ text: searchQuery, code: type })
    }

    setHighlightedSelectedIds(selectedIds)
  }

  useEffect(() => {
    if (query !== '') return

    // clear up the query but not the code so the backend can reset the filters
    fetchFilterSearch({ text: null, code: type })
  }, [query, fetchFilterSearch, type])

  const renderItemCell = ({
    item,
    itemElementProps,
  }: RenderItemProps<DynamicFilterOptionModel>) => (
    <DynamicFilterCell
      key={item.id}
      type={type}
      item={item}
      itemElementProps={itemElementProps}
      handleItemView={getHandleItemView(item)}
    />
  )

  const renderEmptyState = () => (
    <EmptyState title={translations?.noResults?.title} body={translations?.noResults?.body} />
  )

  const renderDropdown = () => {
    return (
      <div className="u-ui-padding-vertical-x-small">
        <InputText
          value={query ?? ''}
          name={`${type}_filter_search`}
          placeholder={translations?.placeholder}
          onChange={handleSearchInput}
        />
        <ScrollableArea maxHeight={SELECT_LARGER_DROPDOWN_MAX_HEIGHT}>
          {listItems.length ? (
            <>
              <SelectableItemList
                name={`${type}_ids`}
                isMultiSelect={selectionType === SelectionType.Multi}
                items={listItems}
                selected={selectedIds}
                renderItem={renderItemCell}
                onItemClick={handleItemClick}
                disableButtonClicks
              />
              {links?.length && (
                <>
                  <Divider />
                  <Cell>
                    {links.map(link => (
                      <Text key={`${type}_link`} html text={link.body} as="p" />
                    ))}
                  </Cell>
                </>
              )}
            </>
          ) : (
            renderEmptyState()
          )}
        </ScrollableArea>
      </div>
    )
  }

  if (isHidden) return null

  return (
    <div className="u-ui-margin-right-regular u-ui-margin-bottom-regular">
      <FilterDropdown
        title={title}
        areAnyOfItemsSelected={!!selectedIds.length}
        testId={`catalog--${type}-filter`}
        minWidth={SELECT_LARGER_DROPDOWN_MIN_WIDTH}
        onFilterClick={handleOnFilterClick}
        onOpen={handleOpenDropdown}
        onClose={handleAfterClose}
        useForceClose={forceClose}
        useChipAsTrigger
      >
        <div className="u-ui-padding-vertical-x-small">{renderDropdown()}</div>
        <FilterSelectionButton onSubmit={handleCloseDropdown} totalItemsCount={totalItemsCount} />
      </FilterDropdown>
    </div>
  )
}

export default SearchFilterDropdown
